import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { OrganizationInviteSelector } from './OrganizationInviteSelector';
import api from '../services/api';
import { showSuccess } from '../utils/toast';
import { Button } from './ui/Button';
import { unwrapResponse } from '../utils/unwrapResponse';
import { UsersIcon } from './Icons';
import { useOrganization } from '../contexts/OrganizationContext';
import { queryKeys } from '../hooks/queries/queryKeys';

export interface PartnerInvitePromptProps {
  organizationId?: string | number;
  organizationName?: string;
}

interface ReminderStatus {
  shouldShowReminder?: boolean;
  hasCreatedInvite?: boolean;
}

export const PartnerInvitePrompt: React.FC<PartnerInvitePromptProps> = ({ organizationId, organizationName }) => {
  // Local override for the three dismiss-style actions below (share/snooze/
  // dismiss) -- none of them re-fetch reminderStatus, they just hide the
  // banner for the rest of this mount, exactly like pre-migration's
  // setShow(false).
  const [dismissed, setDismissed] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(organizationId ? organizationId.toString() : null);
  const navigate = useNavigate();
  const { organizations, selectOrganization } = useOrganization();

  const reminderQuery = useQuery<ReminderStatus>({
    queryKey: queryKeys.partnerInvite.reminderStatus(String(organizationId ?? '')),
    queryFn: async () => {
      const response = await api.get(`/invite-reminders/status?organizationId=${organizationId}`);
      return unwrapResponse<ReminderStatus>(response.data);
    },
    enabled: !!organizationId,
    // Pre-migration: intentional background promotional check, banner
    // degrades to hidden on failure -- no retry wrapper, no console.error.
    retry: false,
  });

  const reminderStatus = reminderQuery.data ?? null;
  const show = !dismissed && reminderStatus?.shouldShowReminder === true;

  const markAsShownMutation = useMutation({
    mutationFn: () => api.post('/invite-reminders/mark-shown'),
    retry: false,
  });

  const dismissMutation = useMutation({
    mutationFn: () => api.post('/invite-reminders/dismiss'),
    retry: false,
  });

  const handleInvite = () => {
    // OrganizationInviteSelector only renders (and selectedOrgId only
    // meaningfully diverges from the ambient org) for multi-org users
    // choosing where to invite. /organizations/settings/members always
    // reflects whichever org is currently active in OrganizationContext,
    // so switch to the chosen org first or that choice is silently dropped.
    if (selectedOrgId && selectedOrgId !== organizationId?.toString()) {
      const target = organizations.find((org) => org.id === selectedOrgId);
      if (target) selectOrganization(target);
    }
    navigate('/organizations/settings/members');
  };
  const markAsShown = async () => {
    try { await markAsShownMutation.mutateAsync(); } catch { /* ignore */ }
  };
  const handleShare = async () => {
    const shareText = `Join me on Helm! ${organizationName || 'Our organization'} uses it to manage tasks together.`;
    const shareUrl = window.location.origin;
    if (typeof navigator.share === 'function') {
      try { await navigator.share({ title: 'Join Helm', text: shareText, url: shareUrl }); await markAsShown(); setDismissed(true); }
      catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`); showSuccess('Invite link copied to clipboard!'); await markAsShown(); setDismissed(true); }
      catch { handleInvite(); }
    }
  };
  const handleSnooze = async () => { await markAsShown(); setDismissed(true); };
  const handleDismiss = async () => {
    try { await dismissMutation.mutateAsync(); setDismissed(true); } catch { /* ignore */ }
  };

  if (!show) return null;

  return (
    <div className="bg-aegean rounded-lg p-4 px-5 mb-5 shadow-lg relative">
      <OrganizationInviteSelector selectedOrgId={selectedOrgId} onChange={setSelectedOrgId} />
      <div className="flex items-center gap-4 flex-col md:flex-row text-center md:text-left">
        <UsersIcon size={32} className="shrink-0 text-white" />
        <div className="flex-1 text-white">
          <strong className="block text-base font-semibold mb-1">Invite your partner!</strong>
          <p className="m-0 text-sm opacity-95 leading-snug">
            {reminderStatus?.hasCreatedInvite
              ? "You've created an invite but it hasn't been accepted yet. Share the link with your partner!"
              : 'Task management works better together. Invite your partner to join you!'}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-col md:flex-row w-full md:w-auto">
          {typeof navigator.share === 'function' ? (
            <Button variant="secondary" size="sm" onClick={handleShare} className="bg-white text-aegean border-none font-semibold w-full md:w-auto">
              Share Invite
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={handleInvite} className="bg-white text-aegean border-none font-semibold w-full md:w-auto">
              Create Invite
            </Button>
          )}
          <button onClick={handleSnooze} className="bg-transparent border-none text-white opacity-90 text-sm cursor-pointer py-2 px-3 rounded-lg hover:opacity-100 hover:bg-white/10 w-full md:w-auto">
            Remind me later
          </button>
          <button onClick={handleDismiss} title="Don't show again"
            className="bg-transparent border-none text-white text-xl py-1 px-2 cursor-pointer opacity-80 hover:opacity-100 absolute top-2 right-2 md:static">
            {'✕'}
          </button>
        </div>
      </div>
    </div>
  );
};
